// PAY1 Slice 3 (docs/build-briefs/PAY1-S3-standup-onboarding.md, sections 2 and 3.5; mockup
// design/mockups/pay1-s3-settings-card-payments.html). Settings > Tabs and payment > Card payments: this location's
// card payments state, the other locations' state names, and "Continue setup with Stripe" for the franchisee's Admin.
//
// The location is the tablet's (the server never takes one from this screen). No account id beyond the mask is ever
// here. The onboarding link is minted by the server on the click, returned once, and handed straight to the
// browser: it is never kept in a variable that outlives the click, never put in the page and never logged. A
// session that may only look sees "View only" and no button. Dark while connected accounts are off: the read
// answers 404 and the whole section stays hidden. Labels and states only (the owner's copy rule). Admin surface:
// pointer and keyboard are fine, and every control is still 44px or taller. All text via textContent.

import { getCardPayments, postCardPaymentsOnboardingLink, postCardPaymentsReturn } from "./api.ts";
import {
  accountLine,
  canContinue,
  onboardingNavigationTarget,
  tenantKind,
  ONE_MORE_THING,
  pillOf,
  REVIEW_POLL_MS,
  REVIEWING_LINE,
  reviewPill,
  reviewStateOf,
  type CardPaymentsArrival,
  type CardPaymentsForLocationDto,
} from "./cardPaymentsModel.ts";

export interface CardPaymentsSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.terminal.view (the Settings read of this panel). */
  canView: () => boolean;
  /** The session holds admin.payments.onboard. */
  canOnboard: () => boolean;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  /** STRIPETAB1: runs in the click, just before the Stripe tab opens (Settings clears the wizard's return marker). */
  beforeOpen?: () => void;
  /** CARDSETUP1: what this location must finish before card payments can start, with where to do it, or null when
   * nothing on this screen's side is missing. */
  missingStep?: () => { line: string; linkLabel: string; open: () => void } | null;
}

export interface CardPaymentsSection {
  /** Reads this location's state fresh and draws it. Hidden for a session that may not look, or when the feature is off. */
  refresh(): Promise<void>;
  /** The browser came back from Stripe (`return`) or Stripe sent it to a link that ran out (`refresh`). */
  handleArrival(arrival: CardPaymentsArrival): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

/** Shown, in red, when the server's answer is not a link the page may follow. */
export const LINK_REFUSED_LINE = "Link not valid";

/** STRIPETAB1. Shown, in red, when the browser would not open the Stripe tab. */
export const TAB_BLOCKED_LINE = "Your browser blocked the new tab. Allow pop-ups for this site and try again.";

/** The Stripe tab opened inside the click. It is pointed at the link once the server answers, or closed. */
export interface OnboardingTab {
  go(url: string): void;
  close(): void;
}

let stripeTabOpened = false;

/**
 * STRIPETAB1. Opens a blank tab; call it synchronously in the click handler, before any await, so the browser does not
 * block it as a pop-up. The opener link is cut so Stripe's page cannot reach this one. Null when the browser refused.
 */
export function openOnboardingTab(): OnboardingTab | null {
  const tab = window.open("about:blank", "_blank");
  if (tab === null) return null;
  try {
    tab.opener = null;
  } catch {
    // Cross-origin guard already holds; nothing to cut.
  }
  stripeTabOpened = true;
  return {
    go: (url) => {
      tab.location.href = url;
    },
    close: () => {
      try {
        tab.close();
      } catch {
        // Already gone.
      }
    },
  };
}

/** The server mints the Account Link for this location on the click. */
export function mintOnboardingLink(
  apiBaseUrl: string,
  tenantId: string,
): Promise<{ status: number; body: { url?: unknown; message?: string; error?: string } }> {
  return postCardPaymentsOnboardingLink(apiBaseUrl, tenantId);
}

/**
 * STRIPETAB1. The one way the browser gets a Stripe onboarding page, shared by Settings, the wizard and the Open Tabs
 * card (CARDNUDGE1). Mints the link (the caller's request), checks it with onboardingNavigationTarget, and points the already
 * open tab at it. A failed mint or a link that fails the check closes the tab and returns the line to show in this tab.
 * The link lives only in this function: it is never stored, shown or logged.
 */
export async function requestOnboardingTarget(
  tab: OnboardingTab,
  mint: () => Promise<{ status: number; body: { url?: unknown; message?: string; error?: string } }>,
  mode: () => "test" | "live" | undefined,
): Promise<{ ok: true } | { ok: false; message: string }> {
  let result: Awaited<ReturnType<typeof mint>>;
  try {
    result = await mint();
  } catch {
    tab.close();
    return { ok: false, message: "Could not open Stripe" };
  }
  if (result.status === 200 && typeof result.body.url === "string") {
    const target = onboardingNavigationTarget(result.body.url, mode());
    if (target === null) {
      tab.close();
      return { ok: false, message: LINK_REFUSED_LINE };
    }
    tab.go(target);
    return { ok: true };
  }
  tab.close();
  return { ok: false, message: result.body.message ?? result.body.error ?? "Something went wrong. Try again." };
}

/**
 * STRIPETAB1: while a page shows "Stripe is reviewing your details", ask the server again every 30 seconds, only while
 * this tab is visible. `active()` says whether the page still shows that state; the loop stops itself when it does not.
 * Returns a function that starts (or restarts) the loop; call it after every draw.
 */
export function reviewPoller(active: () => boolean, again: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedule = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    if (!active()) return;
    timer = setTimeout(() => {
      timer = undefined;
      if (!active()) return;
      // A hidden tab does not read; it asks again at the next beat.
      if (document.visibilityState !== "hidden") again();
      schedule();
    }, REVIEW_POLL_MS);
  };
  return schedule;
}

/**
 * STRIPETAB1. Calls `onReturn` once each time this tab becomes visible again after having been left, and only once a
 * Stripe tab has been opened from this page. Returns the function that stops listening.
 */
export function watchReturnFromStripe(onReturn: () => void): () => void {
  let left = false;
  const check = (): void => {
    if (document.visibilityState === "hidden") {
      left = true;
      return;
    }
    if (!left || !stripeTabOpened) return;
    left = false;
    onReturn();
  };
  const blur = (): void => {
    left = true;
  };
  document.addEventListener("visibilitychange", check);
  window.addEventListener("blur", blur);
  window.addEventListener("focus", check);
  return () => {
    document.removeEventListener("visibilitychange", check);
    window.removeEventListener("blur", blur);
    window.removeEventListener("focus", check);
  };
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

export function mountCardPaymentsSection(
  root: HTMLElement,
  deps: CardPaymentsSectionDeps,
): CardPaymentsSection {
  let view: CardPaymentsForLocationDto | undefined;
  let statusLine = "";
  let errorLine = "";
  let loadSequence = 0;
  let opening = false;
  root.hidden = true;
  // STRIPETAB1: back in this tab after Stripe's tab, read the account once and show where it stands.
  watchReturnFromStripe(() => {
    if (view === undefined || root.hidden || !deps.isCurrent() || opening) return;
    void handleArrival("return");
  });

  // STRIPETAB1: while "Stripe is reviewing your details" shows, read again every 30 seconds while the tab is visible.
  const schedulePoll = reviewPoller(
    () => view !== undefined && !root.hidden && reviewStateOf(view.thisLocation, new Date()) === "reviewing",
    () => {
      if (!opening && deps.isCurrent()) void read();
    },
  );

  function reset(): void {
    loadSequence += 1;
    view = undefined;
    statusLine = "";
    errorLine = "";
    opening = false;
    root.replaceChildren();
    root.hidden = true;
  }

  async function read(): Promise<boolean> {
    if (!deps.canView()) {
      reset();
      return false;
    }
    const sequence = (loadSequence += 1);
    const result = await getCardPayments(deps.apiBaseUrl(), deps.tenantId());
    if (sequence !== loadSequence || !deps.isCurrent()) return false;
    if (result.status === 200 && Array.isArray(result.body.otherLocations)) {
      view = result.body;
      errorLine = "";
      render();
      return true;
    }
    if (result.status === 404 || result.status === 403 || result.status === 409) {
      // Off for this deployment, not allowed, or a device with no location: nothing to show, nothing to say.
      view = undefined;
      root.replaceChildren();
      root.hidden = true;
      return false;
    }
    errorLine = messageOf(result.body);
    render();
    return false;
  }

  function render(): void {
    root.replaceChildren();
    if (view === undefined) {
      if (errorLine === "") {
        root.hidden = true;
        return;
      }
      root.hidden = false;
      root.append(node("h2", "section-title", "Card payments"));
      const error = node("p", "admin-error cp-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "admin-card-payments-error";
      root.append(error);
      return;
    }
    root.hidden = false;
    const wrap = node("div", "cp-section");
    wrap.append(node("h2", "section-title", "Card payments"));
    if (statusLine !== "") {
      const status = node("p", "cp-status-line", statusLine);
      status.setAttribute("role", "status");
      status.dataset["testid"] = "admin-card-payments-status";
      wrap.append(status);
    }

    const here = view.thisLocation;
    const kind = tenantKind(here);
    // STRIPETAB1: after a return from Stripe, "reviewing" and "one more thing" are never the red Restricted.
    const review = reviewStateOf(here, new Date());
    const pill = review === "none" ? pillOf(kind) : reviewPill(here, new Date());
    const locationCard = node("div", `cp-location cp-location-${pill.tone}`);
    locationCard.dataset["testid"] = "admin-card-payments-this-location";
    locationCard.dataset["kind"] = kind;
    const head = node("div", "cp-location-head");
    const titles = node("div", "cp-location-titles");
    const nameRow = node("div", "cp-location-name-row");
    const name = node("span", "cp-location-name", here.locationName);
    name.dataset["testid"] = "admin-card-payments-location-name";
    nameRow.append(name, node("span", "cp-tag", "This location"));
    titles.append(nameRow);
    const account = accountLine(here.masked);
    if (account !== null) {
      const line = node("div", "cp-location-account", account);
      line.dataset["testid"] = "admin-card-payments-account";
      titles.append(line);
    }
    const badge = node("span", `so-badge so-badge-${pill.tone}`);
    badge.dataset["testid"] = "admin-card-payments-pill";
    badge.append(node("span", "so-badge-dot"), pill.label);
    const right = node("div", "cp-location-head-right");
    right.append(badge);
    if (!deps.canOnboard() && canContinue(here) && review !== "reviewing") {
      const viewOnly = node("span", "cp-tag", "View only");
      viewOnly.dataset["testid"] = "admin-card-payments-view-only";
      right.append(viewOnly);
    }
    head.append(titles, right);
    locationCard.append(head);

    if (review === "reviewing") {
      const reviewing = node("p", "cp-fact", REVIEWING_LINE);
      reviewing.setAttribute("role", "status");
      reviewing.dataset["testid"] = "admin-card-payments-reviewing";
      locationCard.append(reviewing);
    }
    if (kind === "restricted" && here.reasonLabel !== null && review === "none") {
      const why = node("div", "cp-fact", `Why: ${here.reasonLabel}`);
      why.dataset["testid"] = "admin-card-payments-reason";
      locationCard.append(why);
    }
    if (kind === "not_set_up") {
      // CARDSETUP1: say what is needed next and link straight to it. Nothing missing here means Snip & Sip starts it.
      const step = deps.missingStep?.() ?? null;
      const next = node(
        "p",
        "cp-fact",
        step !== null
          ? step.line
          : "Nothing is missing here. Ask Snip & Sip to set up card payments for this location.",
      );
      next.dataset["testid"] = "admin-card-payments-next-step";
      locationCard.append(next);
      if (step !== null) {
        const actions = node("div", "cp-actions");
        const link = node("button", "btn-secondary", step.linkLabel);
        link.type = "button";
        link.dataset["testid"] = "admin-card-payments-next-step-link";
        link.addEventListener("click", () => step.open());
        actions.append(link);
        locationCard.append(actions);
      }
    }
    if (here.requirements.length > 0 && review !== "reviewing") {
      locationCard.append(node("div", "cp-heading", review === "more" ? ONE_MORE_THING : "What Stripe still needs"));
      const list = node("ul", "cp-needs");
      list.dataset["testid"] = "admin-card-payments-needs";
      for (const need of here.requirements) {
        const row = node("li", `cp-need ${need.pending ? "cp-need-pending" : "cp-need-done"}`);
        row.dataset["pending"] = String(need.pending);
        row.append(node("span", "cp-need-mark", need.pending ? "Needed" : "Done"), need.label);
        list.append(row);
      }
      locationCard.append(list);
    }
    if (deps.canOnboard() && canContinue(here) && review !== "reviewing") {
      const actions = node("div", "cp-actions");
      const button = node("button", "btn-primary", "Continue setup with Stripe");
      button.type = "button";
      button.dataset["testid"] = "admin-card-payments-continue";
      button.disabled = opening;
      button.addEventListener("click", () => void openStripe(button));
      actions.append(button);
      locationCard.append(actions);
    }
    if (errorLine !== "") {
      const error = node("p", "admin-error cp-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "admin-card-payments-error";
      locationCard.append(error);
    }
    wrap.append(locationCard);

    if (view.otherLocations.length > 0) {
      wrap.append(node("div", "cp-heading", "Your other locations"));
      const list = node("ul", "cp-others");
      list.dataset["testid"] = "admin-card-payments-others";
      for (const other of view.otherLocations) {
        const otherPill = pillOf(tenantKind(other));
        const row = node("li", "cp-other");
        row.dataset["testid"] = "admin-card-payments-other";
        row.append(node("span", "cp-other-name", other.locationName));
        const otherBadge = node("span", `so-badge so-badge-${otherPill.tone}`);
        otherBadge.append(node("span", "so-badge-dot"), otherPill.label);
        row.append(otherBadge);
        list.append(row);
      }
      wrap.append(list);
    }
    root.append(wrap);
    schedulePoll();
  }

  async function openStripe(button: HTMLButtonElement): Promise<void> {
    if (opening) return;
    // Inside the click, before any await: the tab Stripe's page opens in. This tab stays where it is.
    deps.beforeOpen?.();
    const tab = openOnboardingTab();
    errorLine = "";
    if (tab === null) {
      errorLine = TAB_BLOCKED_LINE;
      render();
      return;
    }
    opening = true;
    button.disabled = true;
    const outcome = await requestOnboardingTarget(
      tab,
      () => mintOnboardingLink(deps.apiBaseUrl(), deps.tenantId()),
      () => view?.mode,
    );
    opening = false;
    if (!deps.isCurrent()) return;
    if (!outcome.ok) errorLine = outcome.message;
    render();
  }

  async function handleArrival(arrival: CardPaymentsArrival): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    if (arrival === "refresh") {
      await read();
      statusLine = "Link expired";
      render();
      return;
    }
    // Back from Stripe: the redirect proves nothing, so ask the server to read the account.
    const sequence = (loadSequence += 1);
    if (deps.canOnboard()) {
      const result = await postCardPaymentsReturn(deps.apiBaseUrl(), deps.tenantId());
      if (sequence !== loadSequence || !deps.isCurrent()) return;
      if (result.status === 200 && Array.isArray(result.body.otherLocations)) {
        view = result.body;
        errorLine = "";
        statusLine = tenantKind(result.body.thisLocation) === "enabled" ? "" : "Back from Stripe";
        render();
        return;
      }
      errorLine = messageOf(result.body);
    }
    const kept = errorLine;
    await read();
    if (kept !== "") errorLine = kept;
    statusLine = "Back from Stripe";
    render();
  }

  return {
    async refresh() {
      statusLine = "";
      await read();
    },
    handleArrival,
    reset,
  };
}
